import type { SoftphoneParticipantChildDto, SoftphoneParticipantDto, SoftphoneParticipantSnapshotDto } from 'voxr-shared';

type SoftphoneParticipantRole = 'agent' | 'main' | 'third' | 'unknown';

export type SoftphoneParticipantRow = {
  callSid: string;
  targetNumber?: string;
  status?: string;
  startedAt?: number;
  endedAt?: number;
  muted?: boolean;
  onHold?: boolean;
  legType: SoftphoneParticipantRole;
};

type MapSoftphoneParticipantsOptions = {
  children: SoftphoneParticipantChildDto[];
  participants: SoftphoneParticipantDto[];
  existingRows: SoftphoneParticipantRow[];
  agentCallSid?: string;
  inferEndedAt?: boolean;
  now?: () => number;
};

export function hasSoftphoneParticipantSnapshot(payload?: SoftphoneParticipantSnapshotDto) {
  if (payload?.snapshotUnavailable) return false;
  return Array.isArray(payload?.threeWayChildren) || Array.isArray(payload?.participants);
}

export function normalizeSoftphoneParticipantStatus(status?: string) {
  const value = status?.toString().trim().toLowerCase();
  if (!value) return '--';
  if (value === 'ringing' || value === 'queued' || value === 'initiated') return 'ringing';
  if (value === 'in-progress' || value === 'connected' || value === 'answered') return 'connected';
  if (value === 'completed' || value === 'ended' || value === 'canceled' || value === 'failed' || value === 'busy' || value === 'no-answer') return 'ended';
  return value;
}

function toSoftphoneParticipantTimestamp(value: unknown) {
  if (!value) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function mapSoftphoneParticipants(options: MapSoftphoneParticipantsOptions): SoftphoneParticipantRow[] {
  const childBySid = new Map<string, SoftphoneParticipantChildDto>();
  for (const child of options.children || []) {
    const callSid = child?.callSid?.toString()?.trim();
    if (callSid) childBySid.set(callSid, child);
  }

  const remoteBySid = new Map<string, SoftphoneParticipantDto>();
  for (const participant of options.participants || []) {
    const callSid = participant?.callSid?.toString()?.trim();
    if (callSid) remoteBySid.set(callSid, participant);
  }

  const existingBySid = new Map(
    options.existingRows
      .map(row => [row.callSid?.toString().trim(), row] as const)
      .filter(([callSid]) => !!callSid)
  );
  const agentCallSid = options.agentCallSid?.toString().trim();
  const allSids = new Set([...childBySid.keys(), ...remoteBySid.keys()]);
  const rows = [...allSids].map(callSid => {
    const child = childBySid.get(callSid);
    const remote = remoteBySid.get(callSid);
    const existing = existingBySid.get(callSid);
    const rawStatus = remote?.status || child?.status;
    const status = rawStatus ? normalizeSoftphoneParticipantStatus(String(rawStatus)) : existing?.status || '--';
    const startedAt = toSoftphoneParticipantTimestamp(
      child?.startedAt
      || remote?.startedAt
    ) || existing?.startedAt;
    const endedAt = toSoftphoneParticipantTimestamp(
      child?.endedAt || remote?.endedAt
    ) || existing?.endedAt || (options.inferEndedAt && status === 'ended' && startedAt ? (options.now || Date.now)() : undefined);

    return {
      callSid,
      targetNumber: child?.targetNumber || existing?.targetNumber,
      status,
      startedAt,
      endedAt,
      muted: remote?.muted ?? existing?.muted,
      onHold: remote?.onHold ?? existing?.onHold,
      legType: 'unknown' as const,
    };
  });

  const mainCallSid = rows
    .filter(row => row.callSid !== agentCallSid && !childBySid.has(row.callSid))
    .sort((a, b) => (a.startedAt || Number.MAX_SAFE_INTEGER) - (b.startedAt || Number.MAX_SAFE_INTEGER))[0]?.callSid;

  return rows.map(row => {
    if (row.callSid === agentCallSid) return { ...row, legType: 'agent' };
    if (childBySid.has(row.callSid)) return { ...row, legType: 'third' };
    if (row.callSid === mainCallSid) return { ...row, legType: 'main' };
    return row;
  });
}
